'use client';

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { type Item, type Field, type LinkField } from '@sitecore-content-sdk/nextjs';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';

import { tv } from 'tailwind-variants';
import formConfigJson from './form.config.json';
import SectionList from './SectionList';
import type { FormConfig, CustomErrorMessages, StepConfig } from './types';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { useFormState } from './hooks/useFormState';
import { useSectionManagement } from './hooks/useSectionManagement';
import { useFormNavigation } from './hooks/useFormNavigation';
import { useStepLogic } from './hooks/useStepLogic';
import { useFormChangeHandler } from './hooks/useFormChangeHandler';
import { useAddMoreHandler } from './hooks/useAddMoreHandler';
import { useFormInitialization } from './hooks/useFormInitialization';
import { useFormPersistence } from './hooks/useFormPersistence';
import { useStepVisibility } from './hooks/useStepVisibility';
import { useScrollToStep } from './hooks/useScrollToStep';
import { FormStepView } from './WillsHelperComponents/WillsFormStep/FormStepView';
import {
  SubmissionSuccess,
  type WillsFormSubmissionParams,
} from './WillsHelperComponents/WillsFormStep/SubmissionSuccess';
import { mapFormFieldsToConfig } from './utils/formFieldsMapper';
import { getAddMoreConfig, getAutoShowCondition } from './utils/addMoreConfig';
import { findCardByGroupKey } from './utils/sections';
import type { UseAddMoreFormReturn } from './hooks/useAddMoreForm';
import { useAddMoreForm } from './hooks/useAddMoreForm';
import { useEnableConditionCleanup } from './hooks/useEnableConditionCleanup';
import { UITextProvider } from './hooks/useUIText';
import { sendGTMEvent } from '@next/third-parties/google';
import { toolStartFinishGTMEvent } from 'lib/utils/gtm-utils';
const formConfig = formConfigJson as FormConfig;

interface WillsSiteSettings {
  reCAPTCHASiteKey?: string;
}

interface WillsFormWidgetProps {
  formfields?: CustomForm.Form['fields'] & {
    emailTemplateWithCapacity?: Item;
    emailTemplateWithoutCapacity?: Item;
    emailAcknowledgement?: Item;
    lpaAttachment?: Item;
    successTitleWithoutCapacity?: Field<string>;
    successDescriptionWithoutCapacity?: Field<string>;
    successTitleWithCapacity?: Field<string>;
    successDescriptionWithCapacity?: Field<string>;
    backToWillsButton?: LinkField;
    charityPercentageExceedMessage?: Field<string>;
    charityCashAmountExceedMessage?: Field<string>;
    beneficiaryCashAmountExceedMessage?: Field<string>;
    childrenPercentageExceedMessage?: Field<string>;
  };
  params?: Record<string, unknown>;
}

// Keep step IDs in one place (single source of truth)
const ADD_MORE_STEP_IDS = [
  'step-4-5-relationships',
  'step-4-6-guardian',
  'step-5-4-spouse-partner-children-previous-relationship',
  'step-5-5-guardian',
  'step-6-1-executors',
  'step-7-3-your-asset-business',
  'step-8-1-estate-beneficiaries-charities',
  'step-8-2-estate-beneficiaries-gift-cash',
  'step-8-3-estate-beneficiaries-gift-someone',
] as const;

const ESTATE_BENEFICIARY_STEP_IDS = [
  'step-8-1-estate-beneficiaries-charities',
  'step-8-2-estate-beneficiaries-gift-cash',
  'step-8-3-estate-beneficiaries-gift-someone',
] as const;

type EstateBeneficiaryGroupKey =
  | 'charities-group'
  | 'cash-gift-group'
  | 'cash-gift-to-someone-group';

const ESTATE_BENEFICIARY_STEP_GROUP_MAP: Record<
  (typeof ESTATE_BENEFICIARY_STEP_IDS)[number],
  EstateBeneficiaryGroupKey
> = {
  'step-8-1-estate-beneficiaries-charities': 'charities-group',
  'step-8-2-estate-beneficiaries-gift-cash': 'cash-gift-group',
  'step-8-3-estate-beneficiaries-gift-someone': 'cash-gift-to-someone-group',
};

// Safer: top-level helper hook (no rules-of-hooks lint drama)
function useAddMoreFormForStep(args: {
  stepId: string;
  allSteps: import('./types').StepConfig[];
  values: Record<string, unknown>;
  completedForms: Record<string, import('./types').FormInstance[]>;
  setCompletedForms: React.Dispatch<
    React.SetStateAction<Record<string, import('./types').FormInstance[]>>
  >;
  setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setTouched: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}): UseAddMoreFormReturn {
  const {
    stepId,
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  } = args;

  const step = allSteps.find((s) => s.id === stepId);
  const config = step ? getAddMoreConfig(step) : null;
  const cardResult = config?.groupKey ? findCardByGroupKey(config.groupKey, allSteps) : null;
  const maxInstances = cardResult?.card?.maxInstances;

  return useAddMoreForm({
    stepId,
    step: step || ({ id: '', title: '' } as import('./types').StepConfig),
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
    groupKey: config?.groupKey || '',
    instancePrefix: config?.instancePrefix || '',
    fieldIds: config?.fieldIds || [],
    maxInstances,
    autoShowCondition: config ? getAutoShowCondition(config) : undefined,
  });
}

const WillsFormWidgetInner: React.FC<WillsFormWidgetProps> = ({ formfields, params }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const stepTopRef = useRef<HTMLDivElement | null>(null);
  const willsFormContainerRef = useRef<HTMLDivElement | null>(null);
  const previousStepIdRef = useRef<string | null>(null);

  const updatedFormConfigWithUIText = useMemo(() => {
    return mapFormFieldsToConfig(formConfig, formfields);
  }, [formfields]);

  const { uiText, ...updatedFormConfig } = updatedFormConfigWithUIText;

  const willsEmailCMSData = useMemo(() => {
    if (!formfields) {
      return {
        willCapacityConcernYesOption: undefined,
        willCapacityConcernNoOption: undefined,
        acknowledgement: undefined,
        lpaAttachment: undefined,
        successTitleWithoutCapacity: undefined,
        successDescriptionWithoutCapacity: undefined,
        successTitleWithCapacity: undefined,
        successDescriptionWithCapacity: undefined,
        backToWillsWritingServiceHubLink: undefined,
      };
    }

    return {
      willCapacityConcernYesOption: formfields?.emailTemplateWithCapacity,
      willCapacityConcernNoOption: formfields?.emailTemplateWithoutCapacity,
      acknowledgement: formfields?.emailAcknowledgement,
      lpaAttachment: formfields?.lpaAttachment,
      successTitleWithoutCapacity: formfields?.successTitleWithoutCapacity,
      successDescriptionWithoutCapacity: formfields?.successDescriptionWithoutCapacity,
      successTitleWithCapacity: formfields?.successTitleWithCapacity,
      successDescriptionWithCapacity: formfields?.successDescriptionWithCapacity,
      backToWillsWritingServiceHubLink: formfields?.backToWillsButton,
    };
  }, [formfields]);

  const customErrorMessages: CustomErrorMessages = useMemo(() => {
    if (!formfields) {
      return {
        charityPercentageExceedMessage: undefined,
        charityCashAmountExceedMessage: undefined,
        beneficiaryCashAmountExceedMessage: undefined,
        childrenPercentageExceedMessage: undefined,
      };
    }

    return {
      charityPercentageExceedMessage: formfields?.charityPercentageExceedMessage,
      charityCashAmountExceedMessage: formfields?.charityCashAmountExceedMessage,
      beneficiaryCashAmountExceedMessage: formfields?.beneficiaryCashAmountExceedMessage,
      childrenPercentageExceedMessage: formfields?.childrenPercentageExceedMessage,
    };
  }, [formfields]);

  // Init step graph
  const { allSteps, stepsById } = useFormInitialization({
    formConfig: updatedFormConfig,
    values: {},
  });

  // Form state
  const {
    values,
    setValues,
    errors,
    setErrors,
    touched,
    setTouched,
    completedForms,
    setCompletedForms,
  } = useFormState(updatedFormConfig.localStorageKey, stepsById);

  // Visible steps recalculation
  const { visibleSteps: recalculatedVisibleSteps } = useFormInitialization({
    formConfig: updatedFormConfig,
    values,
  });

  const [currentStepIdState, setCurrentStepIdState] = useState<string>('');

  const sectionManagement = useSectionManagement(
    allSteps,
    stepsById,
    values,
    setValues,
    setErrors,
    setTouched,
    setCurrentStepIdState,
    completedForms,
    setCompletedForms
  );

  const navigation = useFormNavigation({
    allSteps,
    visibleSteps: recalculatedVisibleSteps,
    stepsById,
    values,
    setValues,
    errors,
    setErrors,
    touched,
    setTouched,
    completedForms,
    setCompletedForms,
    currentFormInstanceId: sectionManagement.currentFormInstanceId,
    setCurrentFormInstanceId: sectionManagement.setCurrentFormInstanceId,
    currentSectionId: sectionManagement.currentSectionId,
    setCurrentSectionId: sectionManagement.setCurrentSectionId,
    currentStepIndex: sectionManagement.currentStepIndex,
    setCurrentStepIndex: sectionManagement.setCurrentStepIndex,
    localStorageKey: updatedFormConfig.localStorageKey,
    setIsSubmitting,
    setSubmitted,
    initialStepId: currentStepIdState,
    onStepIdChange: setCurrentStepIdState,
    customErrorMessages,
  });

  useStepVisibility({
    visibleSteps: recalculatedVisibleSteps,
    currentStepId: navigation.currentStepId,
    stepsById,
    setCurrentStepId: navigation.setCurrentStepId,
  });

  useFormPersistence({
    localStorageKey: updatedFormConfig.localStorageKey,
    values,
    currentStepId: navigation.currentStepId,
    completedForms,
  });
  // Clean up instances when enableCondition becomes false
  useEnableConditionCleanup({
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    localStorageKey: updatedFormConfig.localStorageKey,
  });

  useScrollToStep({
    currentStepId: navigation.currentStepId,
    stepTopRef,
  });

  const currentStep = useMemo(() => {
    return stepsById[navigation.currentStepId] || recalculatedVisibleSteps[0] || allSteps[0];
  }, [navigation.currentStepId, stepsById, recalculatedVisibleSteps, allSteps]);

  const stepLogic = useStepLogic({
    currentStep,
    currentStepId: navigation.currentStepId,
    currentSectionId: sectionManagement.currentSectionId,
    allSteps,
    completedForms,
    values,
  });

  const { handleChange } = useFormChangeHandler({
    setValues,
    setTouched,
    setErrors,
    setCompletedForms,
    localStorageKey: updatedFormConfig.localStorageKey,
    currentStepId: navigation.currentStepId,
    completedForms,
  });

  // Create addMore form states (explicit + stable hook order)
  const childrenFormState = useAddMoreFormForStep({
    stepId: 'step-4-5-relationships',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const guardianFormState = useAddMoreFormForStep({
    stepId: 'step-4-6-guardian',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const spousePartnerChildrenFormState = useAddMoreFormForStep({
    stepId: 'step-5-4-spouse-partner-children-previous-relationship',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const spousePartnerGuardianFormState = useAddMoreFormForStep({
    stepId: 'step-5-5-guardian',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const executorFormState = useAddMoreFormForStep({
    stepId: 'step-6-1-executors',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const businessFormState = useAddMoreFormForStep({
    stepId: 'step-7-3-your-asset-business',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const charitiesFormState = useAddMoreFormForStep({
    stepId: 'step-8-1-estate-beneficiaries-charities',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const cashGiftFormState = useAddMoreFormForStep({
    stepId: 'step-8-2-estate-beneficiaries-gift-cash',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const cashGiftToSomeoneFormState = useAddMoreFormForStep({
    stepId: 'step-8-3-estate-beneficiaries-gift-someone',
    allSteps,
    values,
    completedForms,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
  });

  const addMoreFormStates = useMemo(() => {
    const states: Record<string, UseAddMoreFormReturn> = {};

    const byStepId: Record<(typeof ADD_MORE_STEP_IDS)[number], UseAddMoreFormReturn> = {
      'step-4-5-relationships': childrenFormState,
      'step-4-6-guardian': guardianFormState,
      'step-5-4-spouse-partner-children-previous-relationship': spousePartnerChildrenFormState,
      'step-5-5-guardian': spousePartnerGuardianFormState,
      'step-6-1-executors': executorFormState,
      'step-7-3-your-asset-business': businessFormState,
      'step-8-1-estate-beneficiaries-charities': charitiesFormState,
      'step-8-2-estate-beneficiaries-gift-cash': cashGiftFormState,
      'step-8-3-estate-beneficiaries-gift-someone': cashGiftToSomeoneFormState,
    };

    ADD_MORE_STEP_IDS.forEach((stepId) => {
      const step = stepsById[stepId];
      const config = step ? getAddMoreConfig(step) : null;
      const state = byStepId[stepId];

      if (config && state) {
        states[config.groupKey] = state;
      }
    });

    return states;
  }, [
    stepsById,
    childrenFormState,
    guardianFormState,
    spousePartnerChildrenFormState,
    spousePartnerGuardianFormState,
    executorFormState,
    businessFormState,
    charitiesFormState,
    cashGiftFormState,
    cashGiftToSomeoneFormState,
  ]);

  const { handleAdd, handleAddAnother } = useAddMoreHandler({
    sectionInfo: stepLogic.sectionInfo,
    currentFormInstanceId: sectionManagement.currentFormInstanceId,
    currentStepId: navigation.currentStepId,
    stepsById,
    values,
    completedForms,
    setValues,
    setErrors,
    setTouched,
    setCompletedForms,
    setCurrentFormInstanceId: sectionManagement.setCurrentFormInstanceId,
    setCurrentStepIndex: sectionManagement.setCurrentStepIndex,
    setCurrentStepId: navigation.setCurrentStepId,
    validateCurrentStep: navigation.validateCurrentStep,
    currentStep,
    addMoreFormStates,
    customErrorMessages,
  });

  useEffect(() => {
    const previousStepId = previousStepIdRef.current;
    const currentStepId = navigation.currentStepId;

    if (!previousStepId || previousStepId === currentStepId) {
      previousStepIdRef.current = currentStepId;
      return;
    }

    const isPreviousEstateBeneficiaryStep = ESTATE_BENEFICIARY_STEP_IDS.includes(
      previousStepId as (typeof ESTATE_BENEFICIARY_STEP_IDS)[number]
    );

    if (!isPreviousEstateBeneficiaryStep) {
      previousStepIdRef.current = currentStepId;
      return;
    }

    const previousGroupKey =
      ESTATE_BENEFICIARY_STEP_GROUP_MAP[
        previousStepId as (typeof ESTATE_BENEFICIARY_STEP_IDS)[number]
      ];
    const previousState = previousGroupKey ? addMoreFormStates[previousGroupKey] : null;

    previousState?.setShowForm(false);
    previousState?.setEditingInstanceId(null);
    previousState?.setOriginalInstanceData(null);

    const previousStepConfig = stepsById[previousStepId];
    const previousStepFieldIds = getAddMoreConfig(
      previousStepConfig || ({ id: '', title: '' } as StepConfig)
    )?.fieldIds;

    if (previousStepFieldIds && previousStepFieldIds.length > 0) {
      setValues((prev) => {
        const next = { ...prev };
        previousStepFieldIds.forEach((fieldId) => {
          delete next[fieldId];
        });
        return next;
      });

      setErrors((prev) => {
        const next = { ...prev };
        previousStepFieldIds.forEach((fieldId) => {
          delete next[fieldId];
        });
        return next;
      });

      setTouched((prev) => {
        const next = { ...prev };
        previousStepFieldIds.forEach((fieldId) => {
          delete next[fieldId];
        });
        return next;
      });
    }

    previousStepIdRef.current = currentStepId;
  }, [addMoreFormStates, navigation.currentStepId, setErrors, setTouched, setValues, stepsById]);

  useEffect(() => {
    const gtmEvent = toolStartFinishGTMEvent({ toolName: 'online_will', event: 'tool_start' });
    sendGTMEvent(gtmEvent);
  }, []);

  useEffect(() => {
    if (willsFormContainerRef.current) {
      const parent = willsFormContainerRef.current.parentElement;
      if (currentStep.id === 'step-3-sections') {
        if (parent) {
          parent.style.paddingBottom = '0px';
        }
      } else {
        if (parent) {
          parent.removeAttribute('style');
        }
      }
    }
  }, [currentStep]);

  if (!currentStep) {
    return <div>Form configuration error: no visible step.</div>;
  }

  if (submitted) {
    return (
      <SubmissionSuccess
        values={values}
        completedForms={completedForms}
        allSteps={allSteps}
        emailInfo={willsEmailCMSData}
        localStorageKey={updatedFormConfig.localStorageKey}
        params={(params ?? {}) as WillsFormSubmissionParams}
      />
    );
  }

  const { container, formContainer, stepContainer } = TAILWIND_VARIANTS();

  return (
    <UITextProvider uiText={uiText}>
      <div
        className={`${container()} className="scroll-mt-64`}
        id="willsFormContainer"
        data-step-id={currentStep.id}
        ref={willsFormContainerRef}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (stepLogic.isLastStep) {
              navigation.handleNext();
            }
          }}
          noValidate
        >
          <div className={formContainer()}>
            <div ref={stepTopRef} className={`${stepContainer()} scroll-mt-64`}>
              {stepLogic.isSectionStep && currentStep.sections ? (
                <SectionList
                  sections={currentStep.sections}
                  values={values}
                  stepsById={stepsById}
                  completedForms={completedForms}
                  currentSectionId={sectionManagement.currentSectionId}
                  onSectionClick={(sectionId) =>
                    sectionManagement.handleSectionClick(sectionId, navigation.setCurrentStepId)
                  }
                  onSectionEdit={(sectionId, instanceId) =>
                    sectionManagement.handleSectionEdit(
                      sectionId,
                      instanceId,
                      navigation.setCurrentStepId
                    )
                  }
                  currentStepId={currentStep.id}
                  currentStep={currentStep}
                  onBack={navigation.handleBack}
                  onNext={navigation.handleNext}
                  canGoBack={navigation.canGoBack}
                  isSubmitting={isSubmitting}
                  isLastStep={stepLogic.isLastStep}
                  localStorageKey={updatedFormConfig.localStorageKey}
                />
              ) : currentStep.fieldGroups ? (
                <FormStepView
                  allSteps={allSteps}
                  step={currentStep}
                  values={values}
                  errors={errors}
                  touched={touched}
                  canGoBack={navigation.canGoBack}
                  isSubmitting={isSubmitting}
                  isLastStep={stepLogic.isLastStep}
                  isLastStepOfCard={stepLogic.isLastStepOfCard}
                  canAddMore={stepLogic.canAddMore}
                  handleChange={handleChange}
                  setTouched={setTouched}
                  onBack={navigation.handleBack}
                  onNext={navigation.handleNext}
                  onAdd={handleAdd}
                  onAddAnother={handleAddAnother}
                  completedForms={completedForms}
                  stepsById={stepsById}
                  addMoreFormStates={addMoreFormStates}
                  setValues={setValues}
                  setCompletedForms={setCompletedForms}
                  setErrors={setErrors}
                  localStorageKey={updatedFormConfig.localStorageKey}
                  onNavigate={navigation.setCurrentStepId}
                  customErrorMessages={customErrorMessages}
                  onEditInstance={(instanceId) => {
                    const config = getAddMoreConfig(currentStep);
                    if (!config) return;

                    const addMoreFormState = addMoreFormStates[config.groupKey];
                    if (!addMoreFormState) return;

                    const instances = completedForms[config.groupKey] || [];
                    const instance = instances.find((inst) => inst.instanceId === instanceId);
                    if (!instance) return;

                    addMoreFormState.setOriginalInstanceData({ ...instance });
                    addMoreFormState.setEditingInstanceId(instanceId);

                    const updatedValues = { ...values };
                    Object.keys(instance.data).forEach((fieldId) => {
                      if (instance.data[fieldId] !== undefined) {
                        updatedValues[fieldId] = instance.data[fieldId];
                      }
                    });

                    setValues(updatedValues);
                    setErrors({});
                    setTouched({});
                    addMoreFormState.setShowForm(true);
                  }}
                />
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </UITextProvider>
  );
};

const WillsFormWidget: React.FC<WillsFormWidgetProps> = (props) => {
  const { sitecoreContext } = useSitecoreContext();
  const reCAPTCHASiteKey =
    (sitecoreContext?.SiteSettings as WillsSiteSettings)?.reCAPTCHASiteKey ||
    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ||
    '';

  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCAPTCHASiteKey}>
      <WillsFormWidgetInner {...props} />
    </GoogleReCaptchaProvider>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full'],
    formContainer: ['flex', 'flex-col', 'gap-component-section-container-gutter'],
    stepContainer: ['flex', 'flex-col', 'gap-component-section-container-gutter'],
  },
});

export default WillsFormWidget;
